<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import AgenteIaAPI from 'dashboard/api/agenteIa';
import AgenteIaLayout from '../components/AgenteIaLayout.vue';
import { chavesDasFerramentas } from '../habilidades';

const { t } = useI18n();
const lido = ref(null);
const carregando = ref(true);
const erro = ref('');
const mostrarTexto = ref(false);

onMounted(async () => {
  try {
    const { data } = await AgenteIaAPI.prompt();
    lido.value = data;
  } catch (e) {
    erro.value = e?.response?.data?.error || t('AGENTE_IA.PROMPT.ERRO');
  } finally {
    carregando.value = false;
  }
});

const ferramentas = computed(() =>
  chavesDasFerramentas(lido.value?.ferramentas || []).map(chave => t(chave))
);

const secoes = computed(() => {
  const partes = lido.value?.partes;
  if (!partes) return [];
  return [
    { chave: 'IDENTIDADE', itens: [partes.descricao].filter(Boolean) },
    { chave: 'DIRETRIZES', itens: partes.diretrizes },
    { chave: 'PROTECOES', itens: partes.protecoes },
  ];
});
const passouDoTeto = computed(
  () => lido.value && lido.value.caracteres > lido.value.teto
);
</script>

<template>
  <AgenteIaLayout
    :title="t('AGENTE_IA.PROMPT.TITULO')"
    :description="t('AGENTE_IA.PROMPT.DESCRICAO')"
  >
    <p v-if="carregando" class="text-sm text-n-slate-11">
      {{ t('AGENTE_IA.CARREGANDO') }}
    </p>
    <p v-else-if="erro" class="text-sm text-n-ruby-11">{{ erro }}</p>

    <template v-else-if="lido">
      <p v-if="!secoes.length" class="text-sm text-n-slate-11">
        {{ t('AGENTE_IA.PROMPT.SEM_MONTAGEM') }}
      </p>
      <section
        v-for="secao in secoes"
        :key="secao.chave"
        class="flex flex-col gap-2"
      >
        <h2 class="text-sm font-medium text-n-slate-12">
          {{ t(`AGENTE_IA.PROMPT.${secao.chave}.TITULO`) }}
        </h2>
        <p class="text-xs text-n-slate-11">
          {{ t(`AGENTE_IA.PROMPT.${secao.chave}.AJUDA`) }}
        </p>
        <ol
          class="flex flex-col gap-1 pl-5 text-sm list-decimal text-n-slate-12"
        >
          <li v-for="(item, indice) in secao.itens" :key="indice">
            {{ item }}
          </li>
        </ol>
      </section>

      <section class="flex flex-col gap-2">
        <h2 class="text-sm font-medium text-n-slate-12">
          {{ t('AGENTE_IA.PROMPT.FERRAMENTAS.TITULO') }}
        </h2>
        <p class="text-xs text-n-slate-11">
          {{ t('AGENTE_IA.PROMPT.FERRAMENTAS.AJUDA') }}
        </p>
        <div class="flex flex-wrap gap-2">
          <span
            v-for="ferramenta in ferramentas"
            :key="ferramenta"
            class="px-2 py-1 text-xs rounded bg-n-alpha-black2 text-n-slate-12"
          >
            {{ ferramenta }}
          </span>
        </div>
      </section>

      <section class="flex flex-col gap-2 pt-4 border-t border-n-weak">
        <div class="flex items-center justify-between gap-3">
          <h2 class="text-sm font-medium text-n-slate-12">
            {{ t('AGENTE_IA.PROMPT.TEXTO.TITULO') }}
          </h2>
          <button
            class="text-sm text-n-brand"
            @click="mostrarTexto = !mostrarTexto"
          >
            {{
              t(
                mostrarTexto
                  ? 'AGENTE_IA.PROMPT.TEXTO.ESCONDER'
                  : 'AGENTE_IA.PROMPT.TEXTO.MOSTRAR'
              )
            }}
          </button>
        </div>
        <p
          class="text-xs"
          :class="passouDoTeto ? 'text-n-ruby-11' : 'text-n-slate-11'"
        >
          {{
            t('AGENTE_IA.PROMPT.TEXTO.TAMANHO', {
              caracteres: lido.caracteres,
              teto: lido.teto,
            })
          }}
        </p>
        <pre
          v-if="mostrarTexto"
          class="p-4 overflow-x-auto text-xs whitespace-pre-wrap rounded-lg bg-n-alpha-black2 text-n-slate-12"
          >{{ lido.texto }}</pre
        >
      </section>
    </template>
  </AgenteIaLayout>
</template>
